import { STATUSPAGE } from '@repo/ui';
import {
  GetPendingActivitiesPerProfessionalResponse,
  GetPendingActivitiesPerProfessionalSummaryResponse,
  GetAllOrganizationsUserResponse,
  GetAllCoursesUserResponse,
  GetAllSemestersUserResponse,
  GetAllCurricularUnitsUserResponse,
  GetAllGroupsUserResponse,
  GetAllProfessionalsSelectOptionsResponse,
  HandleProfessionalReportResponse,
} from 'domain/models';
import {
  ActivitiesPendingByProfessionalsUseCaseInterface,
  GetAllCurricularUnitsUserUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
  GetAllProfessionalsSelectOptionsUseCaseInterface,
  HandleProfessionalReport,
} from 'domain/usecases';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useMemo, useState } from 'react';

const EXCEL_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

interface IProps {
  handleProfessionalReport?: HandleProfessionalReport;
}

export const useProfessionalReport = (_props?: IProps) => {
  const {
    semesterService,
    curricularUnitService,
    groupService,
    professionalService,
    reportService,
    organizationService,
    courseService,
  } = useService();

  const [statusGet, setStatusGet] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [statusGetSummary, setStatusGetSummary] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [statusExport, setStatusExport] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [statusHandle, setStatusHandle] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  const [organizations, setOrganizations] = useState<
    GetAllOrganizationsUserResponse[]
  >([]);
  const [courses, setCourses] = useState<GetAllCoursesUserResponse[]>([]);
  const [semesters, setSemesters] = useState<GetAllSemestersUserResponse[]>([]);
  const [curricularUnits, setCurricularUnits] = useState<
    GetAllCurricularUnitsUserResponse[]
  >([]);
  const [groups, setGroups] = useState<GetAllGroupsUserResponse[]>([]);
  const [professionals, setProfessionals] = useState<
    GetAllProfessionalsSelectOptionsResponse[]
  >([]);

  const [screenData, setScreenData] =
    useState<GetPendingActivitiesPerProfessionalResponse | null>(null);
  const [summaryData, setSummaryData] =
    useState<GetPendingActivitiesPerProfessionalSummaryResponse | null>(null);
  const [professionalReportResponse, setProfessionalReportResponse] = useState<
    HandleProfessionalReportResponse[]
  >([]);

  const kpis = useMemo(() => {
    if (!summaryData) {
      return {
        professionals: 0,
        pendingActivities: 0,
        exclusive: 0,
        shared: 0,
      };
    }

    const summary =
      summaryData as GetPendingActivitiesPerProfessionalSummaryResponse & {
        total?: number;
        data?: GetPendingActivitiesPerProfessionalResponse['data'];
      };
    const data = Array.isArray(summary.data) ? summary.data : [];

    return {
      professionals: summary.professionalsCount ?? summary.total ?? data.length,
      pendingActivities:
        summary.pendingActivitiesCount ??
        data.reduce((total, item) => total + item.pendingActivitiesCount, 0),
      exclusive:
        summary.exclusiveWorkloadHours ??
        data
          .reduce((total, item) => total + item?.exclusiveWorkloadHours, 0)
          .toFixed(3),
      shared:
        summary.sharedWorkloadHours ??
        data
          .reduce((total, item) => total + item?.sharedWorkloadHours, 0)
          .toFixed(3),
    };
  }, [summaryData]);

  const mapLegacyProfessionalReportParams = (
    params: HandleProfessionalReport.Params,
  ): ActivitiesPendingByProfessionalsUseCaseInterface.Params => ({
    startDate: params.starts,
    endDate: params.ends,
    entityHashId: params.entity,
    courseHashId: params.course,
    semesterHashId: params.semester,
    curricularUnitHashId: params.curricularUnit,
    professionalHashId: params.professional,
    groupHashId: params.groups?.[0],
  });

  const requestGetAllOrganizationsUser = useCallback(async () => {
    return organizationService
      .getAllOrganizationsUser()
      .then((res) => {
        setOrganizations(res);
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao carregar organizações.');
      });
  }, []);

  const requestGetAllCoursesUser = useCallback(async (entityId?: string) => {
    return courseService
      .getAllCoursesUser({ entityId })
      .then((res) => {
        setCourses(res);
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao carregar cursos.');
      });
  }, []);

  const requestGetAllSemestersUser = useCallback(async (entityId: string) => {
    return semesterService
      .getAllSemestersUser({ entityId })
      .then((res) => {
        setSemesters(res);
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao carregar semestres.');
      });
  }, []);

  const requestGetAllCurricularUnitsUser = useCallback(
    async (params: GetAllCurricularUnitsUserUseCaseInterface.Params) => {
      return curricularUnitService
        .getAllCurricularUnitsUser(params)
        .then((res) => {
          setCurricularUnits(res);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao carregar unidades curriculares.');
        });
    },
    [],
  );

  const requestGetAllGroupsUser = useCallback(
    async (params: GetAllGroupsUserUseCaseInterface.Params) => {
      return groupService
        .getAllGroupsUser(params)
        .then((res) => {
          setGroups(res);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao carregar grupos.');
        });
    },
    [],
  );

  const requestGetAllProfessionalsSelectOptions = useCallback(
    async (params: GetAllProfessionalsSelectOptionsUseCaseInterface.Params) => {
      return professionalService
        .getAllProfessionalsSelectOptions(params)
        .then((res) => {
          setProfessionals(res);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao carregar profissionais.');
        });
    },
    [],
  );

  const requestActivitiesPendingScreen = useCallback(
    async (
      params: ActivitiesPendingByProfessionalsUseCaseInterface.Params,
      options: { shouldFetchSummary?: boolean } = {},
    ) => {
      const shouldFetchSummary = options.shouldFetchSummary ?? true;

      setStatusGet(STATUSPAGE.LOADING);

      if (shouldFetchSummary) {
        setStatusGetSummary(STATUSPAGE.LOADING);
      }

      const summaryParams = { ...params };
      delete summaryParams.page;
      delete summaryParams.length;

      return Promise.all([
        reportService.handleActivitiesPendingByProfessionals(params),
        shouldFetchSummary
          ? reportService.getPendingActivitiesPerProfessionalSummary(
              summaryParams,
            )
          : Promise.resolve(null),
      ])
        .then((res) => {
          setScreenData(res[0] as GetPendingActivitiesPerProfessionalResponse);

          if (shouldFetchSummary && res[1]) {
            setSummaryData(
              res[1] as GetPendingActivitiesPerProfessionalSummaryResponse,
            );
          }
        })
        .catch((err) => {
          setScreenData(null);

          if (shouldFetchSummary) {
            setSummaryData(null);
          }

          notifyError(
            err.message || 'Ocorreu um erro ao carregar o relatório.',
          );
        })
        .finally(() => {
          setStatusGet(STATUSPAGE.DONE);

          if (shouldFetchSummary) {
            setStatusGetSummary(STATUSPAGE.DONE);
          }
        });
    },
    [],
  );

  const exportPendingActivities = useCallback(
    async (
      reportFormat: '0' | '1',
      params: ActivitiesPendingByProfessionalsUseCaseInterface.Params,
    ) => {
      setStatusExport(STATUSPAGE.LOADING);

      const exportPromise =
        reportFormat === '0'
          ? reportService.exportPendingActivitiesPerProfessionalPdf(
              params as any,
            )
          : reportService.exportPendingActivitiesPerProfessionalExcel(
              params as any,
            );

      return exportPromise
        .then((res) => {
          const blob = new Blob([res as unknown as ArrayBuffer], {
            type: reportFormat === '0' ? 'application/pdf' : EXCEL_MIME_TYPE,
          });

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => {
          notifyError(
            err.message || 'Ocorreu um erro ao exportar o relatório.',
          );
        })
        .finally(() => {
          setStatusExport(STATUSPAGE.DONE);
        });
    },
    [],
  );

  // Backward compatibility for legacy ProfessionalReport page usage.
  const handlePerformanceReport = useCallback(
    (params: HandleProfessionalReport.Params) => {
      setStatusHandle(STATUSPAGE.LOADING);

      const mappedParams = mapLegacyProfessionalReportParams(params);

      const exportPromise =
        params.type === '0'
          ? reportService.exportPendingActivitiesPerProfessionalPdf(
              mappedParams,
            )
          : reportService.exportPendingActivitiesPerProfessionalExcel(
              mappedParams,
            );

      return exportPromise
        .then((res) => {
          const blob = new Blob([res as unknown as ArrayBuffer], {
            type: params.type === '0' ? 'application/pdf' : EXCEL_MIME_TYPE,
          });

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => {
          notifyError(err.message || 'Ocorreu um erro ao exportar relatório.');
        })
        .finally(() => {
          setStatusHandle(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const handlePerformanceReportJson = useCallback(
    (params: HandleProfessionalReport.Params) => {
      setStatusGet(STATUSPAGE.LOADING);

      const mappedParams = mapLegacyProfessionalReportParams(params);

      return reportService
        .handleActivitiesPendingByProfessionals(mappedParams)
        .then((res) => {
          if (Array.isArray(res)) {
            setProfessionalReportResponse(res);
            return;
          }

          if (
            res &&
            typeof res === 'object' &&
            'data' in res &&
            Array.isArray((res as any).data)
          ) {
            setProfessionalReportResponse((res as any).data);
            return;
          }

          setProfessionalReportResponse([]);
        })
        .catch((err) => {
          setProfessionalReportResponse([]);
          notifyError(err.message || 'Ocorreu um erro ao carregar relatório.');
        })
        .finally(() => {
          setStatusGet(STATUSPAGE.DONE);
        });
    },
    [],
  );

  return {
    statusGet,
    statusGetSummary,
    statusExport,
    statusHandle,
    organizations,
    courses,
    semesters,
    curricularUnits,
    groups,
    professionals,
    screenData,
    professionalReportResponse,
    kpis,
    requestGetAllOrganizationsUser,
    requestGetAllCoursesUser,
    requestGetAllSemestersUser,
    requestGetAllCurricularUnitsUser,
    requestGetAllGroupsUser,
    requestGetAllProfessionalsSelectOptions,
    requestActivitiesPendingScreen,
    exportPendingActivities,
    handlePerformanceReport,
    handlePerformanceReportJson,
  };
};
